<template>
  <div class="progress-bar">
    <template v-for="(step, index) in totalSteps" :key="index">
      <div class="pb-step">
        <div
          :class="[
            'pb-dot',
            index < currentStep ? 'done' : '',
            index === currentStep ? 'current' : '',
            index > currentStep ? 'upcoming' : '',
          ]"
        >
          <svg v-if="index < currentStep" viewBox="0 0 13 10" fill="none">
            <path
              d="M1 5l3.5 3.5L12 1"
              stroke="white"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span v-else>{{ index + 1 }}</span>
        </div>
        <div
          :class="[
            'pb-step-label',
            index < currentStep ? 'done' : '',
            index === currentStep ? 'current' : '',
            index > currentStep ? 'upcoming' : '',
          ]"
        >
          {{ stepLabels[index] }}
        </div>
      </div>

      <div
        v-if="index < totalSteps.length - 1"
        :class="[
          'pb-connector',
          index < currentStep ? 'done' : '',
          index === currentStep ? 'active' : '',
          index > currentStep ? 'upcoming' : '',
        ]"
      />
    </template>
  </div>
</template>

<script setup>
const props = defineProps({
  currentStep: {
    type: Number,
    required: true,
    validator: (value) => value >= 0,
  },
  steps: {
    type: Number,
    required: true,
    validator: (value) => value > 1,
  },
  stepLabels: {
    type: Array,
    default: () => [],
  },
});

const totalSteps = Array.from({ length: props.steps }, (_, i) => i);
</script>

<style scoped>
.progress-bar {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 1rem 1.5rem 1.5rem;
}

.pb-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  position: relative;
  z-index: 1;
}

.pb-connector {
  flex: 1;
  height: 2.5px;
  max-width: 52px;
  margin-top: 14px;
  border-radius: 2px;
  transition: background 0.4s ease;
}

.light-theme .pb-connector.done {
  background: #5dcaa5;
}
.light-theme .pb-connector.active {
  background: linear-gradient(to right, #5dcaa5 50%, #e0e0e0 100%);
}
.light-theme .pb-connector.upcoming {
  background: #e0e0e0;
}

.dark-theme .pb-connector.done {
  background: #5dcaa5;
}
.dark-theme .pb-connector.active {
  background: linear-gradient(to right, #5dcaa5 50%, #2a3a4e 100%);
}
.dark-theme .pb-connector.upcoming {
  background: #2a3a4e;
}

.pb-dot {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 700;
  box-sizing: border-box;
  transition: all 0.3s ease;
}

.pb-dot svg {
  width: 13px;
  height: 13px;
}

.pb-dot.done {
  background: linear-gradient(135deg, #7f77dd 0%, #5dcaa5 100%);
  border: none;
}

.light-theme .pb-dot.current {
  background: white;
  border: 2.5px solid #7f77dd;
  color: #7f77dd;
  box-shadow: 0 0 0 4px rgba(127, 119, 221, 0.15);
}

.dark-theme .pb-dot.current {
  background: #1a2535;
  border: 2.5px solid #afa9ec;
  color: #afa9ec;
  box-shadow: 0 0 0 4px rgba(127, 119, 221, 0.18);
}

.light-theme .pb-dot.upcoming {
  background: white;
  border: 2px solid #d8d8d8;
  color: #c0c0c0;
}

.dark-theme .pb-dot.upcoming {
  background: #1a2535;
  border: 2px solid #2a3a4e;
  color: #3a4a5e;
}

.pb-step-label {
  font-family: 'Poppins', sans-serif;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
  letter-spacing: 0.01em;
  transition: color 0.3s ease;
}

.light-theme .pb-step-label.done {
  color: #5dcaa5;
}
.light-theme .pb-step-label.current {
  color: #7f77dd;
}
.light-theme .pb-step-label.upcoming {
  color: #c0c0c0;
}

.dark-theme .pb-step-label.done {
  color: #5dcaa5;
}
.dark-theme .pb-step-label.current {
  color: #afa9ec;
}
.dark-theme .pb-step-label.upcoming {
  color: #3a4a5e;
}
</style>

<!-- <template>
  <div class="progress-bar">
    <div
      v-for="(step, index) in totalSteps"
      :key="index"
      :class="[
        'step',
        index < currentStep ? 'completed' : '',
        index === currentStep ? 'current' : '',
        index > currentStep ? 'upcoming' : '',
      ]"
    >
      <div class="circle">
        <span v-if="index < currentStep" class="symbol">✓</span>
        <span v-else class="symbol">{{ index + 1 }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  currentStep: {
    type: Number,
    required: true,
    validator: (value) => value >= 0,
  },
  steps: {
    type: Number,
    required: true,
    validator: (value) => value > 1,
  },
});

const totalSteps = Array.from({ length: props.steps }, (_, i) => i);
</script>

<style scoped>
.progress-bar {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 2rem;
}

.step {
  width: 30px;
  height: 30px;
  box-sizing: border-box;
}

.circle {
  width: 100%;
  height: 100%;
  border: 2px solid lightgray;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: sans-serif;
  font-weight: bold;
  font-size: 14px;
  line-height: 1;
}

.symbol {
  display: inline-block;
  height: 16px;
  line-height: 16px;
  text-align: center;
  vertical-align: middle;
}

.completed .circle {
  background-color: #007bff;
  border-color: #48ffcd;
  color: white;
}

.current .circle {
  background-color: white;
  border-color: #007bff;
  color: #007bff;
}

.upcoming .circle {
  border-color: lightgray;
  color: lightgray;
}
</style> -->
